import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { Notice, Pager, errorMessage, secondaryButton } from '../../components/common';
import { t } from '../../i18n';
import type { Message } from './shared';

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string;
  createdAt: string;
  readAt: string | null;
  schoolName: string;
}
interface Preferences {
  email: boolean;
  sms: boolean;
}

/** Everyone's own notifications and how they want to hear from the school. */
export function InboxPage() {
  const queryClient = useQueryClient();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<Message>(null);

  const inbox = useQuery({
    queryKey: ['me', 'notifications', unreadOnly, page],
    queryFn: () =>
      apiList<Notification>('/me/notifications', {
        query: { unreadOnly: unreadOnly ? 'true' : undefined, page, pageSize: 20 },
      }).then((r) => ({ ...r, unread: (r as unknown as { unread: number }).unread ?? 0 })),
  });
  const prefs = useQuery({
    queryKey: ['me', 'notification-preferences'],
    queryFn: () => api<Preferences>('/me/notification-preferences'),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['me'] });
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorMessage(e) });
  const read = useMutation({
    mutationFn: (id: string) => api(`/me/notifications/${id}/read`, { method: 'POST' }),
    onSuccess: refresh,
    onError: fail,
  });
  const readAll = useMutation({
    mutationFn: () => api('/me/notifications/read-all', { method: 'POST' }),
    onSuccess: refresh,
    onError: fail,
  });
  const setPref = useMutation({
    mutationFn: (args: { channel: 'email' | 'sms'; enabled: boolean }) =>
      api('/me/notification-preferences', { method: 'PUT', body: args }),
    onSuccess: refresh,
    onError: fail,
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-2xl font-bold">{t('cm.inbox')}</h1>
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p role="status" className="text-sm">
          {t('cm.unread', { n: inbox.data?.unread ?? 0 })}
        </p>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => {
                setUnreadOnly(e.target.checked);
                setPage(1);
              }}
            />
            {t('cm.unreadOnly')}
          </label>
          <button
            type="button"
            className={secondaryButton}
            disabled={readAll.isPending || (inbox.data?.unread ?? 0) === 0}
            onClick={() => readAll.mutate()}
          >
            {t('cm.markAllRead')}
          </button>
        </div>
      </div>
      {inbox.data?.data.length === 0 && <p className="text-slate-600">{t('cm.emptyInbox')}</p>}
      <ul className="space-y-2">
        {inbox.data?.data.map((n) => (
          <li
            key={n.id}
            className={`rounded-lg border p-3 text-sm ${n.readAt ? 'border-slate-200 bg-white' : 'border-brand-700 bg-brand-50'}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <span className="font-medium">{n.title}</span>
              <span className="text-xs text-slate-600">
                {n.schoolName} · {new Date(n.createdAt).toLocaleString()}
              </span>
            </div>
            <p className="mt-1 whitespace-pre-line">{n.body}</p>
            {!n.readAt && (
              <button
                type="button"
                className={`${secondaryButton} mt-2`}
                aria-label={`${t('cm.markRead')}: ${n.title}`}
                disabled={read.isPending}
                onClick={() => read.mutate(n.id)}
              >
                {t('cm.markRead')}
              </button>
            )}
          </li>
        ))}
      </ul>
      {inbox.data && inbox.data.meta.totalPages > 1 && (
        <Pager page={page} totalPages={inbox.data.meta.totalPages} onPage={setPage} />
      )}

      <section
        aria-label={t('cm.preferences')}
        className="space-y-2 rounded-lg border border-slate-200 bg-white p-4"
      >
        <h2 className="text-lg font-semibold">{t('cm.preferences')}</h2>
        <p className="text-sm text-slate-600">{t('cm.preferencesHint')}</p>
        {(['email', 'sms'] as const).map((channel) => (
          <label key={channel} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={prefs.data?.[channel] ?? true}
              disabled={!prefs.data || setPref.isPending}
              onChange={(e) => setPref.mutate({ channel, enabled: e.target.checked })}
            />
            {channel === 'email' ? t('cm.receiveEmail') : t('cm.receiveSms')}
          </label>
        ))}
      </section>
    </div>
  );
}
